import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { isOidcConfigured } from '../lib/auth';

export type Role = 'admin' | 'ship-request';

export interface UserAccess {
  email: string;
  role: Role;
}

interface UseShipAccessReturn {
  users: UserAccess[];
  loading: boolean;
  error: string | null;
  saving: string | null;
  removing: string | null;
  addUser: (email: string, role: Role) => Promise<void>;
  updateRole: (email: string, role: Role) => Promise<void>;
  removeUser: (email: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useShipAccess(): UseShipAccessReturn {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = isOidcConfigured() ? useAuth() : null; // no AuthProvider in dev/preview bypass
  const [users, setUsers] = useState<UserAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const accessToken = auth?.user?.access_token;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/ship-access');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to load access list');
      setUsers(data.users ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const authHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };

  const addUser = useCallback(
    async (email: string, role: Role) => {
      setSaving(email);
      setError(null);
      try {
        const res = await fetch('/api/ship-access', {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ email, role }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error ?? 'Failed to add user');
        setUsers(data.users ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add user');
      } finally {
        setSaving(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken],
  );

  const updateRole = useCallback(
    async (email: string, role: Role) => {
      setSaving(email);
      setError(null);
      try {
        const res = await fetch('/api/ship-access', {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ email, role }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error ?? 'Failed to update role');
        setUsers(data.users ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update role');
      } finally {
        setSaving(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken],
  );

  const removeUser = useCallback(
    async (email: string) => {
      setRemoving(email);
      setError(null);
      try {
        const res = await fetch('/api/ship-access', {
          method: 'DELETE',
          headers: authHeaders,
          body: JSON.stringify({ email }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error ?? 'Failed to remove user');
        setUsers(data.users ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove user');
      } finally {
        setRemoving(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken],
  );

  return { users, loading, error, saving, removing, addUser, updateRole, removeUser, refetch: load };
}
